import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import Card from "../components/Card"
import { useAuth } from "../auth/AuthProvider"

type LinkRequestResponse = {
  ok?: boolean
  message?: string
  error?: string
}

const LoginPage = () => {
  const navigate = useNavigate()
  const { login, status } = useAuth()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [isSendingMagicLink, setIsSendingMagicLink] = useState(false)
  const [isSendingResetLink, setIsSendingResetLink] = useState(false)

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError(null)
    setInfo(null)
    const result = await login(email, password)
    if (!result.ok) {
      setError(result.message ?? "Unable to sign in.")
      return
    }
    navigate("/", { replace: true })
  }

  const requireEmail = () => {
    if (email.trim()) return true
    setError("Enter your email first.")
    setInfo(null)
    return false
  }

  const handleSendMagicLink = async () => {
    if (!requireEmail()) return

    setError(null)
    setInfo(null)
    setIsSendingMagicLink(true)
    try {
      const response = await fetch("/api/auth/magic-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      })
      const data = (await response.json().catch(() => ({}))) as LinkRequestResponse
      if (!response.ok) {
        setError(data.error ?? "Failed to generate magic link.")
        return
      }
      setInfo(data.message ?? "Magic link generated.")
    } catch {
      setError("Unable to reach the auth service.")
    } finally {
      setIsSendingMagicLink(false)
    }
  }

  const handleSendResetLink = async () => {
    if (!requireEmail()) return

    setError(null)
    setInfo(null)
    setIsSendingResetLink(true)
    try {
      const response = await fetch("/api/auth/password-reset-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      })
      const data = (await response.json().catch(() => ({}))) as LinkRequestResponse
      if (!response.ok) {
        setError(data.error ?? "Failed to generate reset link.")
        return
      }
      setInfo(data.message ?? "Reset link generated.")
    } catch {
      setError("Unable to reach the auth service.")
    } finally {
      setIsSendingResetLink(false)
    }
  }

  useEffect(() => {
    if (status === "authenticated") {
      navigate("/", { replace: true })
    }
  }, [navigate, status])

  return (
    <div className="flex min-h-screen items-center justify-center bg-sand px-6">
      <Card className="w-full max-w-md">
        <p className="text-xl font-semibold text-slate-900">Operator sign in</p>
        <p className="text-sm text-stone">Private access for agency staff only.</p>
        <form onSubmit={handleSubmit} className="mt-6 space-y-4 text-sm">
          <input
            type="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="Email address"
            className="w-full rounded-full border border-line bg-white px-4 py-2"
          />
          <input
            type="password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder="Password"
            className="w-full rounded-full border border-line bg-white px-4 py-2"
          />
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => {
                void handleSendMagicLink()
              }}
              disabled={isSendingMagicLink || isSendingResetLink}
              className="w-full rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold text-slate-700"
            >
              {isSendingMagicLink ? "Generating..." : "Send magic link"}
            </button>
            <button
              type="button"
              onClick={() => {
                void handleSendResetLink()
              }}
              disabled={isSendingMagicLink || isSendingResetLink}
              className="w-full rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold text-slate-700"
            >
              {isSendingResetLink ? "Generating..." : "Forgot password"}
            </button>
          </div>
          {error ? <p className="text-xs text-rose-600">{error}</p> : null}
          {info ? <p className="text-xs text-moss">{info}</p> : null}
          <button
            type="submit"
            disabled={status === "loading"}
            className="w-full rounded-full bg-moss px-4 py-2 font-semibold text-white"
          >
            {status === "loading" ? "Signing in..." : "Sign in"}
          </button>
        </form>
        <p className="mt-4 text-xs text-stone">
          No public signup. Contact an admin to add new team members.
        </p>
      </Card>
    </div>
  )
}

export default LoginPage
